import { FormatterMode as FormatterModeValues, FORMATTER_MODE_VALUES, isFormatterMode, parseFormatterMode, } from "./mode.js";
export { FORMATTER_EXAMPLES } from "./data/formatterExamples.js";
export { FORMATTER_ROUTE_EXAMPLES } from "./data/formatterRouteExamples.js";
export { ALL_FORMAT_SLUG_EXAMPLES } from "./data/formatterAllExamples.js";
export const FormatterMode = FormatterModeValues;
export { FORMATTER_MODE_VALUES, isFormatterMode, parseFormatterMode };
export const HEADING_STYLE_VALUES = ["bold", "h2"];
export const FORMATTER_OPTION_KEYS = [
    "headingStyle",
    "detectHeadings",
    "detectLists",
    "detectColonLabels",
    "splitAfterSentenceEnd",
    "nutritionBlockParsing",
];
export const FORMATTER_TRANSFORM_KEYS = [
    "uppercase",
    "lowercase",
    "capitalize",
    "stripHtml",
    "normalizeWhitespace",
];
export const DEFAULT_TEXT_FORMATTER_OPTIONS = {
    headingStyle: "bold",
    detectHeadings: false,
    detectLists: false,
    detectColonLabels: false,
    splitAfterSentenceEnd: false,
    nutritionBlockParsing: false,
};
export const DEFAULT_WEB_FORMATTER_OPTIONS = {
    headingStyle: "bold",
    detectHeadings: true,
    detectLists: true,
    detectColonLabels: true,
    splitAfterSentenceEnd: false,
    nutritionBlockParsing: false,
};
export const DEFAULT_ECOMMERCE_FORMATTER_OPTIONS = {
    headingStyle: "bold",
    detectHeadings: true,
    detectLists: true,
    detectColonLabels: true,
    splitAfterSentenceEnd: true,
    nutritionBlockParsing: true,
};
export const DEFAULT_FORMATTER_OPTIONS = {
    ...DEFAULT_TEXT_FORMATTER_OPTIONS,
};
const DEFAULT_OPTIONS_BY_MODE = {
    [FormatterModeValues.Text]: DEFAULT_TEXT_FORMATTER_OPTIONS,
    [FormatterModeValues.Web]: DEFAULT_WEB_FORMATTER_OPTIONS,
    [FormatterModeValues.Ecommerce]: DEFAULT_ECOMMERCE_FORMATTER_OPTIONS,
};
function normalizeFormatterMode(mode) {
    return parseFormatterMode(mode, FormatterModeValues.Text);
}
export function getDefaultFormatterOptionsForMode(mode) {
    return DEFAULT_OPTIONS_BY_MODE[normalizeFormatterMode(mode)];
}
export function resolveFormatterConfig({ mode, options, fallbackMode = FormatterModeValues.Text, }) {
    const incoming = options ?? {};
    const base = getDefaultFormatterOptionsForMode(mode ?? fallbackMode);
    return {
        headingStyle: incoming.headingStyle === "h2" ? "h2" : base.headingStyle,
        detectHeadings: typeof incoming.detectHeadings === "boolean"
            ? incoming.detectHeadings
            : base.detectHeadings,
        detectLists: typeof incoming.detectLists === "boolean"
            ? incoming.detectLists
            : base.detectLists,
        detectColonLabels: typeof incoming.detectColonLabels === "boolean"
            ? incoming.detectColonLabels
            : base.detectColonLabels,
        splitAfterSentenceEnd: typeof incoming.splitAfterSentenceEnd === "boolean"
            ? incoming.splitAfterSentenceEnd
            : base.splitAfterSentenceEnd,
        nutritionBlockParsing: typeof incoming.nutritionBlockParsing === "boolean"
            ? incoming.nutritionBlockParsing
            : base.nutritionBlockParsing,
    };
}
export function resolveFormatterOptions(options) {
    return resolveFormatterConfig({ options, fallbackMode: FormatterModeValues.Text });
}
function capitalizeWords(value) {
    return value.replace(/(^|[^\p{L}\p{N}])(\p{L})(\p{L}*)/gu, (_, prefix, first, rest) => `${prefix}${first.toUpperCase()}${rest.toLowerCase()}`);
}
function decodeHtmlEntities(value) {
    const namedEntities = {
        amp: "&",
        lt: "<",
        gt: ">",
        quot: '"',
        apos: "'",
        nbsp: " ",
    };
    return String(value).replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, entity) => {
        if (!entity)
            return match;
        if (entity.startsWith("#x") || entity.startsWith("#X")) {
            const codePoint = Number.parseInt(entity.slice(2), 16);
            if (!Number.isFinite(codePoint))
                return match;
            try {
                return String.fromCodePoint(codePoint);
            }
            catch {
                return match;
            }
        }
        if (entity.startsWith("#")) {
            const codePoint = Number.parseInt(entity.slice(1), 10);
            if (!Number.isFinite(codePoint))
                return match;
            try {
                return String.fromCodePoint(codePoint);
            }
            catch {
                return match;
            }
        }
        return namedEntities[entity.toLowerCase()] ?? match;
    });
}
function stripHtmlToText(value) {
    const withoutScriptAndStyle = String(value)
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
        .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ");
    const withLineBreakHints = withoutScriptAndStyle
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(?:p|div|h[1-6]|li|ul|ol|section|article|header|footer|tr|table)>/gi, "\n");
    const withoutTags = withLineBreakHints.replace(/<\/?[^>]+>/g, " ");
    return decodeHtmlEntities(withoutTags)
        .replace(/\u00a0/g, " ")
        .replace(/[ \t]+/g, " ")
        .replace(/ *\n */g, "\n")
        .trim();
}
function normalizeWhitespaceText(value) {
    return String(value)
        .replace(/\u00a0/g, " ")
        .split(/\r\n|\n|\r/)
        .map((line) => line.replace(/[ \t]+/g, " ").trim())
        .filter(Boolean)
        .join("\n")
        .trim();
}
function applyTransforms(text, transforms) {
    if (!transforms)
        return text;
    let next = String(text);
    if (transforms.stripHtml) {
        next = stripHtmlToText(next);
    }
    if (transforms.normalizeWhitespace) {
        next = normalizeWhitespaceText(next);
    }
    if (transforms.uppercase)
        return next.toUpperCase();
    if (transforms.lowercase)
        return next.toLowerCase();
    if (transforms.capitalize)
        return capitalizeWords(next);
    return next;
}
function preprocessText(text, options, transforms) {
    let t = String(text);
    const looksLikeTimeBoundary = (labelWithColon, nextChar) => {
        const labelCore = labelWithColon.slice(0, -1).trim();
        return /^\d{1,2}$/.test(labelCore) && /\d/.test(nextChar);
    };
    const splitHeadlineList = (line) => {
        if (!line)
            return null;
        // Never re-split existing list lines.
        if (LIST_LINE_REGEX.test(line))
            return null;
        if (/[.!?]/.test(line))
            return null;
        // Keep key/value rows intact.
        if (/:/.test(line))
            return null;
        const tokens = line.split(/\s+/).filter(Boolean);
        if (tokens.length < 4 || line.length > 220)
            return null;
        const cleanToken = (token) => token.replace(/^[("'“”„]+|[)"'“”„.,;:!?]+$/g, "");
        const isAcronym = (token) => /^\p{Lu}{2,}$/u.test(token);
        const isTitlecase = (token) => /^\p{Lu}\p{Ll}/u.test(token);
        const isDigitStart = (token) => /^\d/.test(token);
        const isPercentToken = (token) => /^\d+(?:[.,]\d+)?%$/.test(token);
        const isNumericToken = (token) => /^\d+(?:[.,]\d+)?$/.test(token);
        const startIndices = [0];
        for (let i = 1; i < tokens.length; i += 1) {
            const cleaned = cleanToken(tokens[i]);
            const nextCleaned = cleanToken(tokens[i + 1] ?? "");
            if (!cleaned)
                continue;
            if (isAcronym(cleaned))
                continue;
            if (isTitlecase(cleaned) || isDigitStart(cleaned)) {
                // Keep percentage expressions like "2 %" or "2%" together (always-on safeguard).
                if (isPercentToken(cleaned) || (isNumericToken(cleaned) && nextCleaned === "%")) {
                    continue;
                }
                startIndices.push(i);
            }
        }
        if (startIndices.length < 3)
            return null;
        let maxGap = 0;
        for (let i = 1; i < startIndices.length; i += 1) {
            maxGap = Math.max(maxGap, startIndices[i] - startIndices[i - 1]);
        }
        if (maxGap > 8)
            return null;
        const phrases = [];
        for (let i = 0; i < startIndices.length; i += 1) {
            const start = startIndices[i];
            const end = (startIndices[i + 1] ?? tokens.length) - 1;
            const phrase = tokens.slice(start, end + 1).join(" ");
            if (phrase)
                phrases.push(phrase);
        }
        if (phrases.length < 3)
            return null;
        return `${phrases[0]}\n${phrases
            .slice(1)
            .map((p) => `- ${p}`)
            .join("\n")}`;
    };
    t = t.replace(/\u00a0/g, " ");
    // Always protect percent spacing.
    t = t.replace(/(\d)\s*%/g, "$1 %");
    t = t.replace(/[ \t]-[ \t]/g, " – ");
    t = t.replace(/([.,;:!?%)])([A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ])/g, "$1 $2");
    if (options.splitAfterSentenceEnd) {
        // Split only after sentence-ending punctuation, not inside short title-case phrases like "Men Shoes".
        t = t.replace(/([.!?])\s+(\p{Lu}\p{Ll})/gu, "$1\n\n$2");
    }
    if (options.detectColonLabels) {
        // Recover glued label boundaries like "...olejíchUpozornění:".
        t = t.replace(/(\p{Ll})(\p{Lu}[\p{L}0-9 \t\-()\/]{0,50}:)/gu, "$1\n\n$2");
        // Split inline full label boundaries (e.g. "Company Address: ...", "Label 2: ..."),
        // but avoid splitting inside multi-word labels like "Company Id:" into "Company" + "Id:".
        const splitInlineLabelMatch = (match, prevChar, whitespace, label, offset, source) => {
            const compactLabel = label.trim();
            const nextChar = source
                .slice(offset + match.length)
                .trimStart()
                .charAt(0);
            if (looksLikeTimeBoundary(compactLabel, nextChar)) {
                return `${prevChar}${whitespace}${label}`;
            }
            const labelTokens = compactLabel
                .slice(0, -1)
                .split(/\s+/)
                .filter(Boolean);
            if (labelTokens.length === 3 && /\p{Ll}/u.test(prevChar)) {
                const lastToken = labelTokens[2] ?? "";
                if (!/^\d+$/.test(lastToken)) {
                    return `${prevChar}${whitespace}${label}`;
                }
            }
            const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const previousWordMatch = source
                .slice(0, offset + 1)
                .match(/(\p{L}[\p{L}0-9]*)$/u);
            const previousWord = previousWordMatch?.[1] ?? "";
            const previousLooksLikeLabelPart = /^[\p{Lu}][\p{Ll}]/u.test(previousWord) || /^[A-Z]{2,}$/.test(previousWord);
            if (labelTokens.length === 1 && previousLooksLikeLabelPart) {
                return `${prevChar}${whitespace}${label}`;
            }
            if (labelTokens.length >= 2 && previousLooksLikeLabelPart) {
                const repeatedPrefixRegex = new RegExp(`(?:^|\\s)${escapeRegExp(previousWord)}\\s+(?:[\\p{L}0-9]+)(?:\\s+(?:[\\p{L}0-9]+)){0,1}:`, "gu");
                const repeatedPrefixCount = [...source.matchAll(repeatedPrefixRegex)].length;
                if (repeatedPrefixCount >= 2) {
                    return `${prevChar}${whitespace}${label}`;
                }
            }
            return `${prevChar}\n\n${label}`;
        };
        // 3-word labels are only valid after non-lowercase boundaries to avoid false matches like
        // "... Dubnica Nad Vahom Company Name:" being interpreted as "Vahom Company Name:".
        t = t.replace(/([^\n:])([ \t]+)((?:\p{Lu}[\p{L}0-9]*(?:[ \t]+(?:[\p{L}0-9]+)){2}):)/gu, splitInlineLabelMatch);
        // 1-2 word labels.
        t = t.replace(/([^\n:])([ \t]+)((?:\p{Lu}[\p{L}0-9]*(?:[ \t]+(?:[\p{L}0-9]+)){0,1}):)/gu, splitInlineLabelMatch);
    }
    if (/%/.test(t)) {
        t = t.replace(/\((\d{1,3})\)(?=\s*[A-Za-zÁČĎÉĚÍŇÓŘŠŤÚŮÝŽáčďéěíňóřšťúůýž])/g, "($1 %)");
    }
    t = t.replace(/([^\n])\*(?=[A-Za-zÁČĎÉĚÍŇÓŘŠŤÚŮÝŽáčďéěíňóřšťúůýž])/g, "$1\n\n*");
    t = t.replace(/\*\s*(?=[A-Za-zÁČĎÉĚÍŇÓŘŠŤÚŮÝŽáčďéěíňóřšťúůýž])/g, "* ");
    if (options.detectColonLabels) {
        t = t.replace(/(^|[.!?]\s+)([A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ][\p{L}0-9\s\-()/]{0,60}:)/gu, (match, prefix, label, offset, source) => {
            const nextChar = source
                .slice(offset + match.length)
                .trimStart()
                .charAt(0);
            if (looksLikeTimeBoundary(label, nextChar))
                return `${prefix}${label}`;
            return `${prefix}\n\n${label}`;
        });
    }
    if (options.detectLists && options.splitAfterSentenceEnd) {
        t = t
            .split(/\r\n|\n|\r/)
            .map((line) => splitHeadlineList(line) ?? line)
            .join("\n");
    }
    t = t.replace(/[ \t]+/g, " ");
    t = t.replace(/\n{3,}/g, "\n\n");
    const hasExplicitCaseTransform = Boolean(transforms?.uppercase || transforms?.lowercase || transforms?.capitalize);
    if (!hasExplicitCaseTransform) {
        t = t.replace(/(^|\n\n)([a-záčďéěíňóřšťúůýž])/g, (_, prefix, letter) => `${prefix}${letter.toUpperCase()}`);
    }
    return t.trim();
}
export function normalize(text) {
    const t = String(text)
        .replace(/\u00a0/g, " ")
        .replace(/[ \t]+/g, " ");
    return t
        .replace(/:\s*([A-Za-zÁČĎÉĚÍŇÓŘŠŤÚŮÝŽáčďéěíňóřšťúůýž0-9])/g, (match, next, offset, source) => {
        // Keep no-space only for time-like tokens (e.g. "14:00"), not for labels like "X1: 10".
        let leftIndex = offset - 1;
        while (leftIndex >= 0 && /\d/.test(source.charAt(leftIndex))) {
            leftIndex -= 1;
        }
        const leftDigits = source.slice(leftIndex + 1, offset);
        const leftBoundary = leftIndex < 0 || /[\s([{,;!?]/.test(source.charAt(leftIndex));
        let rightIndex = offset + 1;
        while (rightIndex < source.length && /\d/.test(source.charAt(rightIndex))) {
            rightIndex += 1;
        }
        const rightDigits = source.slice(offset + 1, rightIndex);
        const rightBoundary = rightIndex >= source.length || /[\s)\]},.;!?]/.test(source.charAt(rightIndex));
        const isTimeLikeToken = leftDigits.length >= 1 &&
            leftDigits.length <= 2 &&
            rightDigits.length >= 1 &&
            rightDigits.length <= 2 &&
            leftBoundary &&
            rightBoundary;
        if (isTimeLikeToken && /\d/.test(next))
            return `:${next}`;
        return `: ${next}`;
    })
        .trim();
}
export function splitSentences(text) {
    if (!text)
        return [];
    return text
        .split(/(?<=[.!?])\s+(?=[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ0-9])/g)
        .map((s) => s.trim())
        .filter(Boolean);
}
function findSections(text, options) {
    if (!options.detectHeadings) {
        const trimmed = text.trim();
        return trimmed ? [{ heading: null, body: trimmed }] : [];
    }
    // Detect headings ending with "?" that look like short titles.
    const sections = [];
    const headingRegex = /(^|[.!?]\s+)([A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ][^.!?:]{2,80}?)(\?)(\s+)(?=[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ])/g;
    let lastIndex = 0;
    let match;
    while ((match = headingRegex.exec(text)) !== null) {
        const headingStart = match.index + match[1].length;
        if (headingStart > lastIndex) {
            const before = text.slice(lastIndex, headingStart).trim();
            if (before)
                sections.push({ heading: null, body: before });
        }
        const heading = `${match[2].trim()}?`;
        const afterIndex = headingRegex.lastIndex;
        lastIndex = afterIndex;
        sections.push({ heading, body: null });
    }
    if (lastIndex < text.length) {
        const tail = text.slice(lastIndex).trim();
        if (tail)
            sections.push({ heading: null, body: tail });
    }
    // Merge heading-only with following body.
    const merged = [];
    for (let i = 0; i < sections.length; i++) {
        const s = sections[i];
        if (s.heading && !s.body) {
            const next = sections[i + 1];
            if (next && !next.heading && next.body) {
                merged.push({ heading: s.heading, body: next.body });
                i++;
                continue;
            }
        }
        merged.push(s);
    }
    return merged;
}
function chunkParagraphs(sentences, maxChars = 280) {
    const paras = [];
    let current = "";
    for (const s of sentences) {
        if (!current) {
            current = s;
            continue;
        }
        if ((current + " " + s).length > maxChars) {
            paras.push(current);
            current = s;
        }
        else {
            current += " " + s;
        }
    }
    if (current)
        paras.push(current);
    return paras;
}
function parseNutrition(text) {
    const normalized = String(text)
        .replace(/\r\n?/g, "\n")
        .replace(/[ \t]+/g, " ")
        .trim();
    if (!normalized)
        return null;
    const pairRegex = /(?:^|[\n;])\s*([^:\n]{1,64}):\s*([^\n;]{1,120})(?=$|[\n;])/g;
    const items = [];
    const hasSentenceLikeBreak = (value) => /[.!?]\s+\p{Lu}/u.test(value);
    const isLikelyStructuredValue = (value) => {
        const v = value.trim();
        if (!v || v.length > 48)
            return false;
        if (!/\d/.test(v))
            return false;
        if (hasSentenceLikeBreak(v))
            return false;
        return true;
    };
    let match;
    while ((match = pairRegex.exec(normalized)) !== null) {
        const label = (match[1] ?? "").trim();
        const value = (match[2] ?? "").trim();
        if (!label || !value)
            continue;
        if (label.length > 60)
            continue;
        if (/[.!?]$/.test(label))
            continue;
        const labelWords = label.split(/\s+/).filter(Boolean);
        if (labelWords.length > 10)
            continue;
        if (!isLikelyStructuredValue(value))
            continue;
        items.push({ label, value });
    }
    return items.length >= 3 ? items : null;
}
function makeListFromSentences(sentences) {
    return sentences.map((s) => s.replace(/\s+/g, " ").trim()).filter(Boolean);
}
function escapeHtml(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
function maybeEmphasizeLeadingLabel(text, options) {
    const raw = String(text);
    if (!options.detectColonLabels)
        return escapeHtml(raw);
    const match = raw.match(/^([\p{L}0-9][\p{L}0-9\s\-()\/]{0,60}:)\s+([\s\S]*)$/u);
    if (!match)
        return escapeHtml(raw);
    const label = (match[1] ?? "").trimEnd();
    const value = match[2] ?? "";
    const labelCore = label.slice(0, -1).trim();
    const nextChar = value.trimStart().charAt(0);
    // Keep "14:00" style tokens un-emphasized, but allow labels like "X1: 10 g".
    if (/^\d{1,2}$/.test(labelCore) && /\d/.test(nextChar))
        return escapeHtml(raw);
    return `<strong>${escapeHtml(label)}</strong> ${escapeHtml(value)}`;
}
const LIST_LINE_REGEX = /^\s*([-–•*])\s*(\S.*)$/;
const LIST_MARKER_PREFIX_REGEX = /^\s*[-–•*]\s*/;
const COLLAPSE_SPACES_REGEX = /[ \t]+/g;
function stripListMarker(text) {
    return String(text)
        .replace(LIST_MARKER_PREFIX_REGEX, "")
        .trim();
}
function makeListLineKey(line) {
    const match = String(line).match(LIST_LINE_REGEX);
    if (!match)
        return null;
    const marker = match[1] ?? "";
    const body = String(match[2] ?? "")
        .replace(/[ \t]-[ \t]/g, " – ")
        .replace(COLLAPSE_SPACES_REGEX, " ")
        .trim();
    if (!body)
        return null;
    return `${marker}|${body}`;
}
function collectOriginalListLineCounts(rawText) {
    const counts = new Map();
    for (const line of String(rawText).split(/\r\n|\n|\r/)) {
        const key = makeListLineKey(line);
        if (!key)
            continue;
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
}
function renderHeading(text, style) {
    if (style === "h2")
        return `<h2>${escapeHtml(text)}</h2>`;
    return `<p><strong>${escapeHtml(text)}</strong></p>`;
}
function shouldListFromHeadingBody(sentences, options, hasHeading) {
    if (!hasHeading || !options.detectLists)
        return false;
    return (sentences.length >= 2 &&
        sentences.length <= 6 &&
        sentences.every((s) => s.length >= 1 && s.length <= 180));
}
function formatSection(section, options) {
    const blocks = [];
    const heading = section.heading ? section.heading.trim() : null;
    const body = section.body ? section.body.trim() : "";
    if (!heading && !body)
        return blocks;
    if (heading) {
        blocks.push({ type: "heading", text: heading });
    }
    if (body) {
        if (options.detectLists && options.nutritionBlockParsing) {
            const items = parseNutrition(body);
            if (items) {
                blocks.push({
                    type: "ul",
                    items: items.map((i) => `${i.label}: ${i.value}`),
                });
                return blocks;
            }
        }
        const sentences = splitSentences(body);
        if (shouldListFromHeadingBody(sentences, options, Boolean(heading))) {
            blocks.push({ type: "ul", items: makeListFromSentences(sentences) });
            return blocks;
        }
        const paras = chunkParagraphs(sentences.length ? sentences : [body]);
        for (const p of paras) {
            blocks.push({ type: "p", text: p });
        }
    }
    return blocks;
}
function isHeadingLine(line, options) {
    if (!options.detectHeadings)
        return false;
    const t = line.trim();
    if (!t)
        return false;
    const numberedHeadingMatch = t.match(/^(\d+)\.\s+(.+)$/u);
    const headingCandidate = numberedHeadingMatch ? numberedHeadingMatch[2].trim() : t;
    if (!headingCandidate)
        return false;
    if (!/^[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]/.test(headingCandidate))
        return false;
    if (/[.]$/.test(headingCandidate))
        return false;
    // If it contains multiple sentence enders, do not treat as heading.
    const enders = headingCandidate.match(/[.!?]/g);
    if (enders && enders.length > 1)
        return false;
    if (/[.!?]\s+/.test(headingCandidate.slice(0, -1)))
        return false;
    if (/:/.test(headingCandidate) && !headingCandidate.endsWith(":"))
        return false;
    const words = headingCandidate.split(/\s+/).filter(Boolean);
    return headingCandidate.length <= 90 && words.length <= 12;
}
function isListLine(line) {
    return LIST_LINE_REGEX.test(line);
}
function isRenderableListLine(line) {
    return line.isListMarker && line.isOriginalListLine;
}
function normalizeListLine(line) {
    const lineText = typeof line === "string" ? line : line.text;
    if (!lineText)
        return "";
    if (isListLine(lineText))
        return stripListMarker(lineText);
    return lineText.trim();
}
function listItemTextsFromLines(lines) {
    return lines.map(normalizeListLine).filter(Boolean);
}
function renderListHtml(items, options) {
    if (!items.length)
        return "";
    return `<ul>${items.map((x) => `<li>${maybeEmphasizeLeadingLabel(x, options)}</li>`).join("")}</ul>`;
}
function shouldRenderHeadingBodyAsList(listItems, options) {
    if (!listItems.length)
        return false;
    if (!options.detectLists)
        return false;
    // Deterministic rule: heading body in the same block is list context when lists are enabled.
    return true;
}
function formatLineAware(rawText, options, transforms) {
    const originalListLineCounts = collectOriginalListLineCounts(rawText);
    const normalized = preprocessText(rawText, options, transforms)
        .replace(/<br\s*\/?>/gi, "\n")
        .trim();
    const lines = normalized.split(/\r\n|\n|\r/);
    const blocks = [];
    let current = [];
    for (const line of lines) {
        const clean = line.replace(/[ \t]+/g, " ").trim();
        if (!clean) {
            if (current.length) {
                blocks.push(current);
                current = [];
            }
            continue;
        }
        const listKey = makeListLineKey(clean);
        const availableCount = listKey ? (originalListLineCounts.get(listKey) ?? 0) : 0;
        const isOriginalListLine = Boolean(listKey && availableCount > 0);
        if (listKey && availableCount > 0) {
            originalListLineCounts.set(listKey, availableCount - 1);
        }
        current.push({
            text: clean,
            isListMarker: isListLine(clean),
            isOriginalListLine,
        });
    }
    if (current.length)
        blocks.push(current);
    const html = [];
    const appendStructuredText = (text) => {
        const sections = findSections(normalize(text), options);
        for (const section of sections) {
            const sectionBlocks = formatSection(section, options);
            for (const sectionBlock of sectionBlocks) {
                if (sectionBlock.type === "heading") {
                    html.push(renderHeading(sectionBlock.text, options.headingStyle));
                }
                if (sectionBlock.type === "p") {
                    html.push(`<p>${maybeEmphasizeLeadingLabel(sectionBlock.text, options)}</p>`);
                }
                if (sectionBlock.type === "ul") {
                    html.push(`<ul>${sectionBlock.items
                        .map((item) => `<li>${maybeEmphasizeLeadingLabel(stripListMarker(item), options)}</li>`)
                        .join("")}</ul>`);
                }
            }
        }
    };
    for (let b = 0; b < blocks.length; b += 1) {
        const block = blocks[b];
        const first = block[0] ?? { text: "", isListMarker: false, isOriginalListLine: false };
        const hasHeadingInBlock = block.some((l) => isHeadingLine(l.text, options) ||
            (options.detectColonLabels && l.text.endsWith(":") && isHeadingLine(l.text, options)));
        if (!hasHeadingInBlock) {
            // In non-heading blocks, keep plain lines as paragraphs and render only explicit list-marker lines as lists.
            const hasExplicitListMarkers = options.detectLists && block.some(isRenderableListLine);
            if (hasExplicitListMarkers) {
                let paragraphBuffer = [];
                let listBuffer = [];
                const flushParagraphBuffer = () => {
                    if (!paragraphBuffer.length)
                        return;
                    appendStructuredText(paragraphBuffer.join(" "));
                    paragraphBuffer = [];
                };
                const flushListBuffer = () => {
                    if (!listBuffer.length)
                        return;
                    html.push(renderListHtml(listItemTextsFromLines(listBuffer), options));
                    listBuffer = [];
                };
                for (const line of block) {
                    if (isRenderableListLine(line)) {
                        flushParagraphBuffer();
                        listBuffer.push(line);
                        continue;
                    }
                    flushListBuffer();
                    paragraphBuffer.push(line.text);
                }
                flushParagraphBuffer();
                flushListBuffer();
                continue;
            }
            const asStructuredBlock = block.map((line) => line.text).join("\n");
            if (options.nutritionBlockParsing && parseNutrition(asStructuredBlock)) {
                appendStructuredText(asStructuredBlock);
                continue;
            }
            for (const line of block) {
                appendStructuredText(line.text);
            }
            continue;
        }
        if (block.length === 1 && isHeadingLine(first.text, options) && !first.text.endsWith(":")) {
            html.push(renderHeading(first.text, options.headingStyle));
            continue;
        }
        let buffer = [];
        const flushBuffer = () => {
            if (!buffer.length)
                return;
            const hasExplicitListMarkers = options.detectLists && buffer.some(isRenderableListLine);
            if (hasExplicitListMarkers) {
                let paragraphBuffer = [];
                let listBuffer = [];
                const flushParagraphBuffer = () => {
                    if (!paragraphBuffer.length)
                        return;
                    appendStructuredText(paragraphBuffer.join(" "));
                    paragraphBuffer = [];
                };
                const flushListBuffer = () => {
                    if (!listBuffer.length)
                        return;
                    html.push(renderListHtml(listItemTextsFromLines(listBuffer), options));
                    listBuffer = [];
                };
                for (const line of buffer) {
                    if (isRenderableListLine(line)) {
                        flushParagraphBuffer();
                        listBuffer.push(line);
                        continue;
                    }
                    flushListBuffer();
                    paragraphBuffer.push(line.text);
                }
                flushParagraphBuffer();
                flushListBuffer();
                buffer = [];
                return;
            }
            appendStructuredText(buffer.map((line) => line.text).join(" "));
            buffer = [];
        };
        for (let i = 0; i < block.length; i += 1) {
            const line = block[i] ?? { text: "", isListMarker: false, isOriginalListLine: false };
            const isSectionTitle = options.detectColonLabels && line.text.endsWith(":") && isHeadingLine(line.text, options);
            if (isSectionTitle) {
                flushBuffer();
                html.push(renderHeading(line.text, options.headingStyle));
                const listItems = [];
                for (let j = i + 1; j < block.length; j += 1) {
                    const next = block[j] ?? { text: "", isListMarker: false, isOriginalListLine: false };
                    if (isHeadingLine(next.text, options) ||
                        (options.detectColonLabels &&
                            next.text.endsWith(":") &&
                            isHeadingLine(next.text, options))) {
                        break;
                    }
                    listItems.push(next);
                    i = j;
                }
                if (listItems.length && options.detectLists) {
                    html.push(renderListHtml(listItemTextsFromLines(listItems), options));
                }
                else if (listItems.length) {
                    appendStructuredText(listItems.map((item) => item.text).join(" "));
                }
                continue;
            }
            if (isHeadingLine(line.text, options)) {
                flushBuffer();
                if (!line.text.endsWith(":")) {
                    const listItems = [];
                    for (let j = i + 1; j < block.length; j += 1) {
                        const next = block[j] ?? { text: "", isListMarker: false, isOriginalListLine: false };
                        if (isHeadingLine(next.text, options) ||
                            (options.detectColonLabels &&
                                next.text.endsWith(":") &&
                                isHeadingLine(next.text, options))) {
                            break;
                        }
                        listItems.push(next);
                        i = j;
                    }
                    if (shouldRenderHeadingBodyAsList(listItems, options)) {
                        html.push(renderHeading(line.text, options.headingStyle));
                        html.push(renderListHtml(listItemTextsFromLines(listItems), options));
                    }
                    else if (listItems.length) {
                        const sectionBlocks = formatSection({ heading: line.text, body: listItems.map((item) => item.text).join(" ") }, options);
                        for (const sectionBlock of sectionBlocks) {
                            if (sectionBlock.type === "heading") {
                                html.push(renderHeading(sectionBlock.text, options.headingStyle));
                            }
                            if (sectionBlock.type === "p") {
                                html.push(`<p>${maybeEmphasizeLeadingLabel(sectionBlock.text, options)}</p>`);
                            }
                            if (sectionBlock.type === "ul") {
                                html.push(`<ul>${sectionBlock.items
                                    .map((item) => `<li>${maybeEmphasizeLeadingLabel(stripListMarker(item), options)}</li>`)
                                    .join("")}</ul>`);
                            }
                        }
                    }
                    else {
                        html.push(renderHeading(line.text, options.headingStyle));
                    }
                }
                else {
                    html.push(renderHeading(line.text, options.headingStyle));
                }
            }
            else {
                buffer.push(line);
            }
        }
        flushBuffer();
    }
    return html.join("\n");
}
export function formatText({ text, mode, options = {}, transforms, output = "html", }) {
    const transformed = applyTransforms(text, transforms);
    const plainTextOutput = output === "text";
    if (plainTextOutput) {
        return String(transformed).replace(/\u00a0/g, " ").trim();
    }
    const resolved = resolveFormatterConfig({
        mode,
        options,
        fallbackMode: FormatterModeValues.Text,
    });
    const preprocessed = preprocessText(transformed, resolved, transforms);
    const hasLineBreaks = /\r\n|\n|\r/.test(String(preprocessed)) ||
        /<br\s*\/?>/i.test(String(preprocessed)) ||
        LIST_LINE_REGEX.test(String(text));
    if (hasLineBreaks) {
        return formatLineAware(transformed, resolved, transforms);
    }
    const normalized = normalize(preprocessed);
    const sections = findSections(normalized, resolved);
    const blocks = [];
    for (const section of sections) {
        blocks.push(...formatSection(section, resolved));
    }
    return blocks
        .map((block) => {
        if (block.type === "heading")
            return renderHeading(block.text, resolved.headingStyle);
        if (block.type === "p")
            return `<p>${maybeEmphasizeLeadingLabel(block.text, resolved)}</p>`;
        if (block.type === "ul")
            return `<ul>${block.items
                .map((item) => `<li>${maybeEmphasizeLeadingLabel(item, resolved)}</li>`)
                .join("")}</ul>`;
        return "";
    })
        .join("\n");
}
